import { Box, Heading, Text } from '@chakra-ui/react';
import { AngularNavLink } from '@texas/components/shared/AngularNavLink';
import { createArticleStyles as styles } from '@texas/resources/theme/styles/entityWorkspace';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateArticle } from './useCreateArticle';

export function CreateArticleSummary() {
  const { t } = useTranslation();
  const createArticle = useCreateArticle();
  const { concept, articleCount, newCount, importCount } = useMemo(() => {
    const concept = createArticle.resources.concepts.data?.find(
      (item) => item.id === createArticle.conceptId,
    );
    const articleCount = createArticle.creationTargets.length;
    const newCount = createArticle.creationTargets.filter(
      ({ itemCode }) => createArticle.availability.data?.[itemCode]?.isValid,
    ).length;
    const importCount = createArticle.creationTargets.filter(
      ({ itemCode }) =>
        createArticle.availability.data?.[itemCode]?.errorType ===
          'InUseByErp' && createArticle.options[itemCode]?.importErp,
    ).length;
    return {
      concept,
      articleCount,
      newCount,
      importCount,
    };
  }, [
    createArticle.resources.concepts.data,
    createArticle.conceptId,
    createArticle.creationTargets,
    createArticle.availability.data,
    createArticle.options,
  ]);

  const countLabels = [
    t('article.copy.newArticleCount', { count: newCount }),
    t('article.create.erpImportCount', { count: importCount }),
  ];
  if (createArticle.ignoredCount > 0) {
    countLabels.push(
      t('article.copy.ignoredClashingItemCodes', {
        count: createArticle.ignoredCount,
      }),
    );
  }
  const unavailableCount = articleCount - newCount - importCount;
  if (unavailableCount > 0) {
    countLabels.push(
      t('article.create.unavailableCount', { count: unavailableCount }),
    );
  }

  return (
    <Box sx={styles.summaryContent} tabIndex={0}>
      <Heading as="h2" sx={styles.summaryTitle}>
        {t('article.create.summary')}
      </Heading>
      <Box sx={styles.summaryCustomer}>
        <Text sx={styles.muted}>{t('article.copy.customer')}</Text>
        {createArticle.customer ? (
          <AngularNavLink
            to="customer"
            navobj={{ customerId: createArticle.customerId }}
            sx={styles.summaryCustomerLink}
          >
            {createArticle.customer.label}
          </AngularNavLink>
        ) : (
          <Text sx={styles.summarySecondary}>{t('general.none')}</Text>
        )}
        <Text sx={styles.summarySecondary}>
          {t('general.concept')} · {concept?.name ?? t('general.none')}
        </Text>
      </Box>
      <Box sx={styles.summaryGroup}>
        <Box sx={styles.summaryCountRow}>
          <Text sx={styles.summaryCount}>{articleCount}</Text>
          <Text sx={styles.summaryCountLabel}>
            {t('article.create.articlesToCreate', { count: articleCount })}
          </Text>
        </Box>
        {articleCount > 0 &&
          createArticle.availability.data &&
          !createArticle.availability.loading &&
          !createArticle.availability.error && (
            <Text sx={styles.summarySecondary}>{countLabels.join(' · ')}</Text>
          )}
      </Box>
    </Box>
  );
}
